import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { clipboxExportTrackingAtom } from '@/atoms/clipboxExport-progress.atom';
import { execute } from '@/graphql/execute';
import type { ClipboxExportFormat } from '@/graphql/graphql';
import { startClipboxExport } from '@/queries/clipboxExport.query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ClipBoxLayerOption {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Project the export runs against — used by the polling tracker hook
   *  mounted at the project shell to scope which trackers it owns. */
  projectId: string;
  /** All POINTCLOUD layer options the user can pick as the source. */
  pointCloudLayers: ClipBoxLayerOption[];
  /** Initial selection — first option if not provided or invalid. */
  defaultLayerId: string | null;
  /** Snapshot of the clipbox at the moment the dialog opened. */
  clipBox: {
    center: [number, number, number];
    halfSize: [number, number, number];
    rotation: [number, number, number, number];
  };
}

export default function ClipBoxExportDialog({
  open,
  onOpenChange,
  projectId,
  pointCloudLayers,
  defaultLayerId,
  clipBox,
}: Props) {
  const { t } = useTranslation('project');

  const resolveInitialLayerId = () => {
    if (defaultLayerId && pointCloudLayers.some((l) => l.id === defaultLayerId)) {
      return defaultLayerId;
    }
    return pointCloudLayers[0]?.id ?? '';
  };

  const [layerId, setLayerId] = useState<string>(resolveInitialLayerId);
  const [format, setFormat] = useState<ClipboxExportFormat>('LAZ');
  const [name, setName] = useState<string>('');
  const [, setTrackers] = useAtom(clipboxExportTrackingAtom);

  // Reset selections whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    const id = resolveInitialLayerId();
    setLayerId(id);
    setFormat('LAZ');
    const layerName = pointCloudLayers.find((l) => l.id === id)?.name ?? '';
    setName(layerName ? `${layerName} (clipped)` : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Update default name whenever the source layer selection changes
  useEffect(() => {
    const layerName = pointCloudLayers.find((l) => l.id === layerId)?.name ?? '';
    setName(layerName ? `${layerName} (clipped)` : '');
  }, [layerId, pointCloudLayers]);

  const mutation = useMutation({
    mutationFn: async () => {
      return execute(startClipboxExport, {
        input: {
          sourceLayerId: layerId,
          format,
          clipBox: {
            center: clipBox.center,
            halfSize: clipBox.halfSize,
            rotation: clipBox.rotation,
          },
          label: name.trim(),
        },
      });
    },
    onSuccess: (data) => {
      const batchId = data.startClipboxExport;
      const sourceLayerName = pointCloudLayers.find((l) => l.id === layerId)?.name ?? '';
      // Long-lived loading toast; useClipboxExportProgress resolves it
      // when the parent job transitions to PROCESSED or FAILED.
      const toastId = toast.loading(
        t('clipboxExport.runningToast', { name: sourceLayerName }),
        { duration: Infinity },
      );
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: layerId,
          sourceLayerName,
          format,
        },
      ]);
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err.message || t('clipboxExport.failedToStart'));
    },
  });

  const isFormValid = layerId !== '' && name.trim() !== '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('clipboxExport.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <div className='min-w-0 space-y-4 py-2'>
          {/* Source layer */}
          <div className='space-y-2'>
            <Label>{t('clipboxExport.sourceLabel')}</Label>
            <Select
              value={layerId}
              onValueChange={setLayerId}
              disabled={pointCloudLayers.length === 0 || mutation.isPending}>
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pointCloudLayers.map((layer) => (
                  <SelectItem key={layer.id} value={layer.id}>
                    {layer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Format */}
          <div className='space-y-2'>
            <Label>{t('clipboxExport.formatLabel')}</Label>
            <RadioGroup
              value={format}
              onValueChange={(v) => setFormat(v as ClipboxExportFormat)}
              className='flex gap-4'
              disabled={mutation.isPending}>
              <label className='flex cursor-pointer items-center gap-2'>
                <RadioGroupItem value='LAZ' id='format-laz' />
                <span className='text-sm'>LAZ</span>
              </label>
              <label className='flex cursor-pointer items-center gap-2'>
                <RadioGroupItem value='E57' id='format-e57' />
                <span className='text-sm'>E57</span>
              </label>
            </RadioGroup>
            <p className='text-xs text-muted-foreground'>{t('clipboxExport.formatHint')}</p>
          </div>

          {/* Name */}
          <div className='space-y-2'>
            <Label>{t('clipboxExport.nameLabel')}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('clipboxExport.namePlaceholder')}
              disabled={mutation.isPending}
            />
          </div>

          {/* Info note */}
          <p className='text-xs text-muted-foreground'>{t('clipboxExport.infoNote')}</p>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            type='button'
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}>
            {t('clipboxExport.cancel')}
          </Button>
          <Button
            type='button'
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !isFormValid}>
            {t('clipboxExport.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
